/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { memo, useCallback } from "react";

import { HotspotController } from "./HotspotController";
import {
  clearMarkers,
  createHotspotMarkers,
  type HotspotRecord,
} from "../utils/mapMarkers";

/**
 * Props for MemoizedMapHotspot component
 */
export type MemoizedHotspotControllerProps = {
  hotspot: HotspotRecord;
  mapboxgl: any;
  mapInstanceRef: React.MutableRefObject<any>;
};

/**
 * Memoized wrapper component for MapHotspot
 * Prevents re-renders and re-animations when hotspot data hasn't changed
 */
export const MemoizedHotspotController = memo<MemoizedHotspotControllerProps>(
  ({ hotspot, mapboxgl, mapInstanceRef }) => {
    const add = useCallback(() => {
      if (!mapboxgl || !mapInstanceRef.current) {
        return () => {};
      }

      const markers = createHotspotMarkers(
        mapInstanceRef.current,
        mapboxgl.Marker,
        [hotspot],
      );

      return () => clearMarkers(markers);
    }, [hotspot.id, hotspot.lat, hotspot.lng, mapboxgl, mapInstanceRef]);

    return <HotspotController key={hotspot.id} {...hotspot} add={add} />;
  },
  (prevProps, nextProps) =>
    // Custom comparison function to prevent re-renders
    // Only re-render if the hotspot's essential properties change
    prevProps.hotspot.id === nextProps.hotspot.id &&
    prevProps.hotspot.lat === nextProps.hotspot.lat &&
    prevProps.hotspot.lng === nextProps.hotspot.lng &&
    prevProps.hotspot.active === nextProps.hotspot.active &&
    prevProps.mapboxgl === nextProps.mapboxgl &&
    prevProps.mapInstanceRef === nextProps.mapInstanceRef,
);

MemoizedHotspotController.displayName = "MemoizedHotspotController";
